import { useMatches } from '@tanstack/react-router'
import type { AgentRouteContext } from '@/gen/cortado/v1/agent_pb'

/**
 * Maps URL path segments to entity type names used by the chat system.
 */
function mapSegmentToEntityType(segment: string): string | null {
  const mapping: Record<string, string> = {
    leads: 'inquiry',
    events: 'event',
    contacts: 'contact',
    invoices: 'invoice',
    quotes: 'quote',
    companies: 'company',
  }
  return mapping[segment] ?? null
}

/**
 * Extracts entity context from the current route.
 *
 * When the user is viewing an entity detail page (e.g., /leads/uuid),
 * this hook returns the entity type and ID so the chat can understand
 * contextual requests like "update to in progress" without the user
 * specifying which entity.
 *
 * The viewer timezone is always present, including on non-detail pages, so
 * date-only operating data is evaluated against the viewer's local calendar.
 */
export function routeContextForPath(pathname: string, timeZone: string): AgentRouteContext {
  const normalizedTimeZone = timeZone.trim() || 'UTC'

  // Extract entity type and ID from path like "/leads/uuid" or "/events/uuid"
  const segments = pathname.split('/').filter(Boolean)
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

  for (let i = 0; i < segments.length; i++) {
    if (uuidRegex.test(segments[i]) && i > 0) {
      const parentSegment = segments[i - 1]
      const entityType = mapSegmentToEntityType(parentSegment)
      if (entityType) {
        return {
          entityType,
          entityId: segments[i],
          path: pathname,
          timeZone: normalizedTimeZone,
          $typeName: 'cortado.v1.AgentRouteContext',
        } as AgentRouteContext
      }
    }
  }

  return {
    entityType: '',
    entityId: '',
    path: pathname,
    timeZone: normalizedTimeZone,
    $typeName: 'cortado.v1.AgentRouteContext',
  } as AgentRouteContext
}

export function useRouteContext(): AgentRouteContext {
  const matches = useMatches()
  const pathname = matches[matches.length - 1]?.pathname ?? ''
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  return routeContextForPath(pathname, timeZone)
}
